<template>
    <page title="competitionApprovalEvClMatrix" :textHeight="(WindowSize.y - 272) + 'px'">
        <template #pageContainer>
			<v-form ref="form" v-model="valid">
				<v-row v-if="Competition"><v-col>
					<v-textarea outlined
						hide-details="auto"
						height="60px"
						:readonly="isViewMode"
						class="mt-0 pt-0"
						clearable
						dense
						:label="$t('text.additionaltInfo')"
						v-model="Competition.additionalComment">
					</v-textarea>
				</v-col></v-row>
				<v-row v-if="!isViewMode && getClasses!=null"><v-col cols="12"  sm="5" lg="5">
						<v-autocomplete outlined dense
							hide-details="auto"
							clearable
							:label="$t(`text.copyFrom`)"
							:items="getClasses"
							item-text="longname"
							item-value="id"
							v-model="copyFrom">
						</v-autocomplete>
					</v-col>
					<v-col cols="12"  sm="5" lg="5">
						<v-autocomplete outlined dense
							hide-details="auto"
							clearable
							:label="$t(`text.to`)"
							item-text="longname"
							item-value="id"
							:items="getClasses"
							v-model="copyTo">
						</v-autocomplete>
					</v-col>
					<v-col cols="12"  sm="2" lg="2">
						<v-btn
							class="mr-4"
							:disabled="!copyFrom || !copyTo"
							@click="copyEvents">
							{{ $t(`text.copy`) }}
						</v-btn>
				</v-col></v-row>
				<v-row><v-col class="data-wrapper">
					<div class="blanker" ref="blanker"></div>
					<table :class="{ 'grid-error': hasError }" :style="{height: getTableHeight + 'px'}">
					<thead>
						<tr>
							<td rowspan="2"></td>
							<td class="header-td header-all">
								<div class="header-outer"><div class="header-inner">
									<div class="header-element">{{$t('text.all')}}</div>
								</div></div>
							</td>
							<td class="header-td" ref="header"
								v-for="(cl, i) in getClasses" :key="i">
								<div class="header-outer"><div class="header-inner">
									<div class="header-element">{{cl.longname}}</div>
								</div></div>
							</td>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td class="side-header-td header-all">
								{{$t('text.all')}}
							</td>
							<td></td>
							<td v-for="(cl, i) in getClasses" :key="i" class="checkbox unselectable all-classes">
									<div>
										<c-checkbox type="checkbox"
											:readonly="isViewMode"
											:checkOnClickedMousover="true"
											@change="onBoxAllClassesChecked(cl.id)"
											:value="isAnyClassCheked(cl.id)"
											:indeterminate="isClassPartiallyChecked(cl.id)"
											:key="'all-' + cl.id">
										</c-checkbox>
									</div>
							</td>
						</tr>
						<tr v-for="(ev, i) in getEvents" :key="i">
							<td class="side-header-td" ref="sideHeader">
								{{ev.longname}}
								<template v-if="ev.definitionEventId == null">
									<template v-if="ev.measurement !== 'h'">({{ev.courseLength/1000}}{{$t('text.km')}})</template>
									<template v-else>({{formatTime(ev.courseLength)}})</template>
								</template>
							</td>
							<td class="checkbox unselectable all-events">
								<div>
									<c-checkbox type="checkbox"
										:readonly="isViewMode"
										:checkOnClickedMousover="true"
										@change="onBoxAllEventsChecked(ev.id)"
										:value="isAnyEventCheked(ev.id)"
										:indeterminate="isEventPartiallyChecked(ev.id)"
										:key="'all-' + ev.id">
									</c-checkbox>
								</div>
							</td>
							<td v-for="(cl, i) in getClasses" :key="i" class="checkbox unselectable">
								<div>
									<c-checkbox type="checkbox"
										:readonly="isViewMode"
										:checkOnClickedMousover="true"
										@change="onBoxChecked(ev.id, cl.id)"
										:value="isChkBoxCheked(ev.id, cl.id)"
										:key="ev.id + '-' + cl.id">
									</c-checkbox>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
				</v-col></v-row>
            </v-form>
            <spinner :loading="loading"></spinner>
        </template>
		<template #pageActions>
			<pop-up v-model="confirmOverwrite"
				:show="showConfirmOverwrite"
				title="dialogText.reallySave"
				text="editCompApprovalHINT"
				:props="confirmSaveData"
				type="confirm"
				width="70%"
				@close="onConfirmClose">
			</pop-up>
			<v-row>
				<v-col>
					<v-btn
						class="mr-4"
						v-if="isViewMode"
						@click="backToOverview">
						{{ $t(`text.backToOverview`) }}
					</v-btn>
					<v-btn
						class="mr-4"
						v-if="isViewMode"
						@click="saveAndNext">
						{{ $t(`text.next`) }}
					</v-btn>
					<v-btn
						v-if="!isViewMode"
						class="mr-4"
						:disabled="!valid || loading"
						@click="save">
						{{ $t(`text.save`) }}
					</v-btn>
					<v-btn
						v-if="!isViewMode"
						class="mr-4"
						:disabled="!valid || loading"
						@click="saveAndNext">
						{{ $t(`text.createAndNext`) }}
					</v-btn>
					<v-btn
						:disabled="loading"
						class="mr-4"
						@click="back">
						{{ $t(`text.backButton`) }}
					</v-btn>
				</v-col>
			</v-row>
        </template>
    </page>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';

import compApprovalActions from '../store/actions/compApproval';
import namespace from '../store/namespace';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import CCheckbox from '../general/CCheckbox.vue';
import PopUp from '../general/PopUp.vue';

import modelState from '../mixins/modelState';

export default {
	name: 'CompetitionApprovalEventClassesMatrix',
	mixins: [modelState],
	components: {
		Page,
		Spinner,
		CCheckbox,
		PopUp,
	},
	data() {
		return {
			valid: true,
			loading: false,
			eventClasses: [],
			hasError: false,
			copyFrom: null,
			copyTo: null,
			pristineComp: {
				eventClasses: [],
			},
			confirmOverwrite: 1,
			confirmSaveData: null,
			showConfirmOverwrite: false,
		};
	},
	watch: {
		loading() {
			this.$nextTick(this.setGridHeight());
		},
		'WindowSize.y': function () {
			this.setGridHeight();
		},
	},
	async mounted() {
		this.loading = true;
		if (this.CompApprLinks.length === undefined || this.CompApprLinks.length === 0) {
			await this.GET_COMP_APPROVAL_LINKS();
		}

		const link = this.CompApprLinks.find(x => x.rel === 'get_by_id');
		if (link) {
			await this.GET_COMP_BY_ID({ link, cGuid: this.cGuid });
			this.readFromDTO();
		}

		this.loading = false;
		this.setGridHeight();
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.COMPETITIONAPPROVAL, ['CompApprLinks', 'Competition']),
		cGuid() {
			return this.$route.params.cGuid || null;
		},
		getClasses() {
			return this.Competition ? this.Competition.classes : null;
		},
		getEvents() {
			return this.Competition ? this.Competition.events : null;
		},
		getTableHeight() {
			return this.WindowSize.y >= 900 ? this.WindowSize.y - 435 : 500;
		},
		isChkBoxCheked: state => (eId, cId) => !!state.eventClasses.find(x => x.classId === cId && x.eventId === eId),
		isAnyClassCheked: state => cId => !!state.eventClasses.find(x => x.classId === cId),
		isAnyEventCheked: state => eId => !!state.eventClasses.find(x => x.eventId === eId),
		isClassPartiallyChecked: state => (cId) => {
			if (!state.eventClasses.length) return false;
			const count = state.eventClasses.reduce((a, x) => (x.classId === cId ? a + 1 : a), 0);
			return ![0, state.getEvents.length].includes(count);
		},
		isEventPartiallyChecked: state => (eId) => {
			if (!state.eventClasses.length) return false;
			const count = state.eventClasses.reduce((a, x) => (x.eventId === eId ? a + 1 : a), 0);
			return ![0, state.getClasses.length].includes(count);
		},
		isViewMode() {
			return this.$route.name === 'showcompAppEvClMatrix';
		},
		isPristine() {
			const eventClasses = this.eventClasses.map(x => x.eventId + x.classId).sort();
			return this.pristineComp.eventClasses.length == eventClasses.length
				&& JSON.stringify(this.pristineComp.eventClasses) == JSON.stringify(eventClasses);
		},
		isStatusResetting() {
			return !this.isPristine && this.Competition?.competitionState != 2;
		},
	},
	methods: {
		...mapActions(namespace.COMPETITIONAPPROVAL, [
			compApprovalActions.GET_COMP_APPROVAL_LINKS,
			compApprovalActions.GET_COMP_BY_ID,
			compApprovalActions.UPDATE_COMP_APPROVAL,
		]),
		setGridHeight() {
			if (this.$refs.blanker) {
				if (this.$refs.sideHeader !== undefined && this.$refs.sideHeader.length !== 0) {
					this.$refs.blanker.style.width = `${this.$refs.sideHeader[0].clientWidth}px`;
				} else {
					this.$refs.blanker.style.width = 0;
				}
				if (this.$refs.header !== undefined && this.$refs.header.length !== 0) {
					this.$refs.blanker.style.height = `${this.$refs.header[0].clientHeight}px`;
				} else {
					this.$refs.blanker.style.height = 0;
				}
			}
		},
		classSelectionChanged() {
			if (this.classes.length != 0) {
				this.classes.sort((a, b) => (a.order > b.order ? 1 : -1));
			}
			this.eventSelectionChanged();
		},
		eventSelectionChanged() {
			if (this.events.length != 0) {
				this.events.sort((a, b) => (a.order > b.order ? 1 : -1));
			}
		},
		onBoxAllClassesChecked(cId) {
			const i = this.eventClasses.findIndex(x => x.classId === cId);
			if (i !== -1) {
				this.eventClasses = this.eventClasses.filter(x => x.classId !== cId);
			} else {
				this.getEvents.forEach(e => this.eventClasses.push({ eventId: e.id, classId: cId }));
			}
		},
		onBoxAllEventsChecked(eId) {
			const i = this.eventClasses.findIndex(x => x.eventId === eId);
			if (i !== -1) {
				this.eventClasses = this.eventClasses.filter(x => x.eventId !== eId);
			} else {
				this.getClasses.forEach(c => this.eventClasses.push({ eventId: eId, classId: c.id }));
			}
		},
		onBoxChecked(eId, cId) {
			const i = this.eventClasses.findIndex(x => x.classId === cId && x.eventId === eId);
			if (i !== -1) {
				this.eventClasses.splice(i, 1);
			} else {
				this.eventClasses.push({ eventId: eId, classId: cId });
			}
			this.hasError = false;
		},
		formatTime(time) {
			const sec = (time / 1000);
			const hours = Math.floor(sec / (60 * 60));

			const divisorForMinutes = sec % (60 * 60);
			const minutes = Math.floor(divisorForMinutes / 60);
			return `${String(hours).padStart(2, 0)}:${String(minutes).padStart(2, 0)}`;
		},
		readFromDTO() {
			this.eventClasses = JSON.parse(JSON.stringify(this.Competition.eventClasses));
			this.pristineComp.eventClasses = JSON.parse(JSON.stringify(this.Competition.eventClasses.map(x => x.eventId + x.classId).sort()));
		},
		async create() {
			this.loading = true;

			if (this.valid) {
				let link = null;
				link = this.CompApprLinks.find(x => x.rel === 'update_comp');
				this.Competition.eventClasses = this.eventClasses;

				if (link) {
					try {
						this.modelState = {};
						await this.UPDATE_COMP_APPROVAL({ newCompApproval: this.Competition, link, cGuid: this.cGuid });
					} catch (e) {
						console.log(e);
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
		},
		async save() {
			this.confirmSaveData = { name: 'competitionapproval' };
			await this.requestSave();
		},
		async saveAndNext() {
			if (!this.isViewMode) {
				this.confirmSaveData = {
					name: 'compApprovalAttachments',
					params: { cGuid: this.cGuid },
				};

				await this.requestSave();
			} else {
				this.$router.push({
					name: 'showcompApprovalAttachments',
					params: { cGuid: this.cGuid },
				});
			}
		},
		backToOverview() {
			this.$router.push({
				name: 'competitionapproval',
			});
		},
		back() {
			this.$router.go(-1);
		},
		copyEvents() {
			let found = false;
			do {
				found = this.eventClasses.findIndex(x => x.classId === this.copyTo);
				if (found !== -1) this.eventClasses.splice(found, 1);
			} while (found !== -1);
			const ev = this.eventClasses.filter(x => x.classId === this.copyFrom).map(y => ({ classId: this.copyTo, eventId: y.eventId }));
			ev.forEach((x) => {
				if (!this.eventClasses.find(y => y.classId === x.classId && y.eventId === x.eventId)) this.eventClasses.push({ ...x });
			}, this);
			this.copyFrom = null;
			this.copyTo = null;
		},
		async requestSave() {
			this.loading = true;
			if (this.isStatusResetting) {
				this.showConfirmOverwrite = true;
			} else {
				await this.create();
				this.$router.push(this.confirmSaveData);
			}
			this.loading = false;
		},
		async onConfirmClose(nextRoute) {
			this.loading = true;
			this.showConfirmOverwrite = false;
			this.confirmSaveData = null;
			if (this.confirmOverwrite) {
				await this.create();
				this.$router.push(nextRoute);
			}
			this.loading = false;
		},
	},
};
</script>
<style lang="scss" scoped>
table {
	display: block;
	overflow: auto;
    max-width: fit-content;
	max-width:  -moz-fit-content;
	border-spacing:0;
}

.data-wrapper {
	position: relative;
}
.blanker {
	background-color:#fff;
	position: absolute;
	z-index: 1;
}

thead td {
	position: sticky;
	top: 0;
}

.header-td {
	background-color: #fff;
}

.header-outer {
	display: grid;
	width: 25px;
	overflow: hidden;
}
.header-inner {
	padding: 50% 0;
	height: 0;
}

.side-header-td {
	width: 1%;
    white-space: nowrap;
    padding-right: 15px;
	position: sticky;
	left: 0;
	background-color: #fff;
	border-color: #fff;
}

.header-all {
	font-weight: bolder;
}

.all-events {
	border-right:1px solid black;
}

.all-classes {
	border-bottom:1px solid black;
}

.checkbox {
	padding-left:5px;
}

.header-element {
	display: block;
  transform-origin: top left;
  transform: rotate(-90deg) translate(-100%);
  margin-top: -50%;
  white-space: nowrap;
}

.grid-error {
	border: solid 1px red;
}

.unselectable {
   -moz-user-select: -moz-none;
   -khtml-user-select: none;
   -webkit-user-select: none;
   -ms-user-select: none;
   user-select: none;
}
</style>
